import React, { ReactNode } from "react";
import Page, { PageProps } from "../../../core/ui/pages/Page";
import {
  Spin,
  Layout,
  Collapse,
  Typography,
  Col,
  Row,
  Space,
  Popover,
  Button
} from "antd";
import { ICaseLawDocumentDetailsPresenter } from "../../presenter/CaseLawDocumentDetailsPresenter";
import OrganisationIndexTag from "../../../organisations/ui/components/OrganisationIndexTag";

import CaseLawDocumentAttachmentTable from "../components/CaseLawDocumentAttachmentTable";
import { CaseLawDocumentStatus, CourtCaseType } from "../../common/Constants";
import CaseLawDocumentStatusSteps from "../components/CaseLawDocumentStatusSteps";
import CaseLawDocumentToolbar from "../components/CaseLawDocumentToolbar";
import CaseLawDocumentSummary from "../components/CaseLawDocumentSummary";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  PrinterOutlined
} from "@ant-design/icons";
import CaseLawDocumentCourtCaseDescription from "../components/CaseLawDocumentCourtCaseDescription";
import CaseLawDocumentList from "../components/CaseLawDocumentList";
import HeadingLabel from "../../../core/ui/components/HeadingLabel";
const { Title } = Typography;
const { Panel } = Collapse;

export interface Props extends PageProps {}

export interface State extends ICaseLawDocumentDetailsPresenter {}
const { Content, Sider } = Layout;

const renderPanel = (
  title: string,
  component: ReactNode,
  classNames?: string
) => {
  return (
    <div className="layout-panel">
      <Collapse
        defaultActiveKey={"0"}
        key={`details-panel-${title}`}
        className={classNames}
      >
        <Panel
          header={
            <span
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 500
              }}
            >
              {title}
            </span>
          }
          key="0"
        >
          <div style={{ marginLeft: 24 }}>{component}</div>
        </Panel>
      </Collapse>
    </div>
  );
};
export default class CaseLawDocumentDetailsPage extends React.Component<
  Props,
  State
> {
  private subscriptionId: number = 0;

  constructor(props: any) {
    super(props);
    this.state = { ...props, current: 0 };
  }

  componentDidMount() {
    this.subscriptionId = this.state.store.subscribe((result) => {
      return this.setState(() => result);
    });
  }

  componentWillUnmount() {
    this.state.store.unsubscribe(this.subscriptionId);
  }
  render() {
    const {
      translate,
      caseLawDocument,
      attachmentTypes,
      attachmentContentTypes,
      caseLawDocumentAttachments,
      createAttachment,
      setFileValue,
      uploadedFile,
      updateAttachment,
      removeAttachment,
      indexHierarchyList,
      publishCaseLawDocument,
      archiveCaseLawDocument,
      revertCaseLawDocumentStatus,
      showCaseLawDocumentUpdatePage,
      showCaseLawDocumentDetailsPage,
      user,
      invalidatedCaseLawDocuments,
      canEdit,
      deleteCaseLawDocument
    } = this.state;

    let hasConnectedCases =
      caseLawDocument?.courtCaseList?.filter(
        (courtCase) => courtCase.type === CourtCaseType.CONNECTED
      )?.length > 0;

    function renderConnectedCases() {
      return caseLawDocument?.courtCaseList?.map((courtCase, index) => {
        return (
          courtCase.type === CourtCaseType.CONNECTED && (
            <li
              className="case-law-document-list-item"
              key={`case-list-item-${index}`}
            >
              {courtCase.caseDescription
                ? `${courtCase.courtCaseNumber} - ${courtCase.caseDescription}`
                : `${courtCase.courtCaseNumber}`}
            </li>
          )
        );
      });
    }

    function renderPrimaryCase() {
      let primaryCase = caseLawDocument?.courtCaseList?.find(
        (courtCase) => courtCase.type === CourtCaseType.PRIMARY
      );

      return (
        <div style={{ marginTop: 20 }}>
          <CaseLawDocumentCourtCaseDescription
            courtCase={primaryCase}
            translate={translate}
            readonly
            showDecisions
            decisions={
              primaryCase?.decisions ? [primaryCase?.decisions?.[0]] : undefined
            }
          />
        </div>
      );
    }

    return (
      <Page {...this.props} className={"page-wrapper"}>
        <Spin spinning={this.state.loaders.caseLawDocumentLoader}>
          <Row gutter={[16, 16]} className="details-container">
            <Col xs={24} sm={24} md={10} xl={5}>
              <Title
                level={1}
                style={{ marginBottom: 30 }}
                className="case-law-document-title"
              >
                {translate("editors.case_law_documents.case_law_document")}
              </Title>
            </Col>
            <Col xs={24} sm={24} md={14} xl={19}>
              <Row justify="space-around" align="middle">
                {canEdit?.(caseLawDocument.organisationId) ? (
                  <Col span={24}>
                    <div className={"png-steps-wrapper no-print"}>
                      <CaseLawDocumentStatusSteps
                        status={caseLawDocument.status}
                        onClick={() => {
                          showCaseLawDocumentDetailsPage(
                            caseLawDocument.invalidatedBy
                          );
                        }}
                        translate={translate}
                      />
                    </div>
                  </Col>
                ) : null}
              </Row>
              <Row
                justify="end"
                align="middle"
                gutter={[16, 16]}
                className="no-print"
              >
                <Col span={1}>
                  <Button
                    size="large"
                    type="text"
                    icon={
                      <PrinterOutlined
                        style={{
                          fontSize: "24px"
                        }}
                      />
                    }
                    onClick={() => {
                      window.print();
                    }}
                  ></Button>
                </Col>
              </Row>
            </Col>
          </Row>
          <Layout>
            <Layout>
              {canEdit?.(caseLawDocument.organisationId) &&
                caseLawDocument?.status !== CaseLawDocumentStatus.ARCHIVED && (
                  <Sider
                    className={"content-container dashboard-sider no-print"}
                    breakpoint="xl"
                    collapsible
                    collapsedWidth="0"
                    width={"20%"}
                    style={{ height: 250 }}
                  >
                    <CaseLawDocumentToolbar
                      translate={translate}
                      user={user}
                      caseLawDocument={caseLawDocument}
                      onArchiveClick={archiveCaseLawDocument}
                      onEditClick={showCaseLawDocumentUpdatePage}
                      onPublishClick={publishCaseLawDocument}
                      onUndoClick={revertCaseLawDocumentStatus}
                      deleteCaseLawDocument={deleteCaseLawDocument}
                    />
                  </Sider>
                )}
              <Layout className={"dashboard-content-wrapper"}>
                <Content className="site-layout-background dashboard-content-container">
                  <Row gutter={[16, 8]} className={"mx-0 mb-4"}>
                    <Col span={24}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {renderPanel(
                          translate("editors.case_law_documents.basic_data"),
                          <>
                            <Row>
                              <Col xs={24} sm={24}>
                                <CaseLawDocumentSummary
                                  label={translate(
                                    "editors.case_law_documents.summary"
                                  )}
                                  text={
                                    caseLawDocument.summaryTranslation ??
                                    caseLawDocument.summary
                                  }
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={24} sm={24}>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_law_documents.status"
                                  )}
                                />
                                {caseLawDocument.status ===
                                CaseLawDocumentStatus.INVALIDATED ? (
                                  <>
                                    <Popover
                                      content={
                                        caseLawDocument.invalidationReason ??
                                        "-"
                                      }
                                    >
                                      <p>
                                        <span style={{ marginRight: 4 }}>
                                          <CloseCircleTwoTone twoToneColor="#ea2102" />
                                        </span>
                                        {translate(
                                          "editors.case_law_documents.invalid_status_description"
                                        )}
                                      </p>
                                    </Popover>
                                  </>
                                ) : (
                                  <>
                                    <p>
                                      <span style={{ marginRight: 4 }}>
                                        <CheckCircleTwoTone twoToneColor="#299545" />
                                      </span>
                                      {translate(
                                        "editors.case_law_documents.valid_status_description"
                                      )}
                                    </p>
                                  </>
                                )}
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={24} sm={24}>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_law_documents.organisation_name"
                                  )}
                                />
                                <p>{caseLawDocument?.organisationName}</p>
                              </Col>
                            </Row>
                            {caseLawDocument.formattedDate ? (
                              <Row>
                                <Col xs={24} sm={24}>
                                  <HeadingLabel
                                    text={translate(
                                      "editors.case_law_documents.date"
                                    )}
                                  />
                                  <p>{caseLawDocument.formattedDate}</p>
                                </Col>
                              </Row>
                            ) : null}
                            <Row>
                              <Col xs={24} sm={24}></Col>
                              <Col xs={24} sm={24}>
                                {renderPrimaryCase()}
                              </Col>
                            </Row>
                            {caseLawDocument.notes?.length > 0 ? (
                              <Row gutter={[16, 8]} style={{ marginTop: 32 }}>
                                <Col xs={24} sm={24}>
                                  <HeadingLabel
                                    text={translate(
                                      "editors.case_law_documents.notes"
                                    )}
                                  />
                                  <p>{caseLawDocument.notes}</p>
                                </Col>
                              </Row>
                            ) : null}
                          </>,
                          "panel-summary"
                        )}

                        {renderPanel(
                          translate(
                            "editors.case_law_documents.classification"
                          ),
                          <>
                            <HeadingLabel
                              text={translate(
                                "editors.case_law_documents.index_data"
                              )}
                            />
                            <ul className="case-law-document-list">
                              {indexHierarchyList.map((item, index) => (
                                <li
                                  className="case-law-document-list-item"
                                  key={`case-index-list-item-${index}`}
                                >
                                  <OrganisationIndexTag
                                    router={this.props.router}
                                    indexHierarchyList={item.path}
                                    indexId={item.id}
                                  />
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {hasConnectedCases
                          ? renderPanel(
                              translate(
                                "editors.case_law_documents.connected_court_cases"
                              ),
                              <>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_law_documents.court_cases"
                                  )}
                                />
                                <ul className="case-law-document-list">
                                  {renderConnectedCases()}
                                </ul>
                              </>
                            )
                          : null}
                        {invalidatedCaseLawDocuments?.length > 0
                          ? renderPanel(
                              translate(
                                "editors.case_law_documents.invalidated_case_law_docs"
                              ),
                              <CaseLawDocumentList
                                translate={translate}
                                loaders={
                                  this.state.loaders.caseLawDocumentLoader
                                }
                                caseLawDocumentList={
                                  invalidatedCaseLawDocuments
                                }
                                showCaseLawDocumentDetails={
                                  showCaseLawDocumentDetailsPage
                                }
                                user={user}
                                showSearchResult={true}
                              />
                            )
                          : null}
                        {renderPanel(
                          translate("editors.case_law_documents.attachments"),
                          <CaseLawDocumentAttachmentTable
                            caseLawDocument={caseLawDocument}
                            translate={translate}
                            attachmentTypes={attachmentTypes}
                            attachmentContentTypes={attachmentContentTypes}
                            caseLawDocumentAttachments={
                              caseLawDocumentAttachments
                            }
                            createAttachment={createAttachment}
                            setFileValue={setFileValue}
                            uploadedFile={uploadedFile}
                            updateAttachment={updateAttachment}
                            removeAttachment={removeAttachment}
                            referenceLoader={
                              this.state.loaders.attachmentLoader
                            }
                            editable={false}
                            canDownload={canEdit}
                          />,
                          "no-print"
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Content>
              </Layout>
            </Layout>
          </Layout>
        </Spin>
      </Page>
    );
  }
}
